import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetRiskAssessmentQuery } from '../../../features/schedule/riskAssessmentSlice';
import { useLazyGetRiskAssessmentReportQuery } from '../../../features/schedule/scheduleSlice';
import dayjs from 'dayjs';
import {
    RiskAssessmentItem,
    CompetentPerson,
    SignatureData,
    ReportReceiptSignature,
    SafetyPrecautionsChecklist,
    RiskAssessmentMode
} from '../types';
import {
    INITIAL_RISK_ASSESSMENT_ITEMS,
    INITIAL_SAFETY_PRECAUTIONS_CHECKLIST
} from '../constants';

export const useRiskAssessment = () => {
    const location = useLocation();
    const { scheduleID, mode, formData } = location.state || {};

    const currentMode: RiskAssessmentMode = mode || 'create';
    const isViewMode = currentMode === 'view';

    // 添加下載功能
    const [getRiskAssessmentReport] = useLazyGetRiskAssessmentReportQuery();

    // 基本資料狀態
    const [site, setSite] = useState('');
    const [workContent, setWorkContent] = useState('');
    const [contractor, setContractor] = useState('');
    const [subcontractor, setSubcontractor] = useState('');

    // 合資格人士資料
    const [competentPerson, setCompetentPerson] = useState<CompetentPerson>({
        name: '',
        certificateNumber: '',
        expiryDate: '',
    });

    // 簽名狀態
    const [assessmentSignature, setAssessmentSignature] = useState<SignatureData>({
        name: '',
        dateTime: '',
        sign: '',
        signUrl: ''
    });

    const [reportReceiptSignature, setReportReceiptSignature] = useState<ReportReceiptSignature>({
        name: '',
        position: '',
        dateTime: '',
        sign: '',
        signUrl: ''
    });

    // 風險評估項目
    const [riskAssessmentItems, setRiskAssessmentItems] = useState<RiskAssessmentItem[]>(INITIAL_RISK_ASSESSMENT_ITEMS);

    // 安全預防措施清單
    const [safetyPrecautionsChecklist, setSafetyPrecautionsChecklist] = useState<SafetyPrecautionsChecklist>(INITIAL_SAFETY_PRECAUTIONS_CHECKLIST);

    // 獲取現有數據
    const { data: existingRiskAssessment, isLoading, error } = useGetRiskAssessmentQuery(
        scheduleID || '',
        {
            skip: !scheduleID || (currentMode !== 'edit' && currentMode !== 'view'),
            refetchOnMountOrArgChange: true
        }
    );

    // 處理傳入的數據
    useEffect(() => {
        if (currentMode === 'create' && formData) {
            setSite(formData.site || '');
            setWorkContent(formData.workContent || '');
            setContractor(formData.contractor || '');
        }
    }, [currentMode, formData]);

    // 載入現有數據
    useEffect(() => {
        if ((currentMode === 'edit' || currentMode === 'view') && existingRiskAssessment && !isLoading) {
            setSite(existingRiskAssessment.site || '');
            setWorkContent(existingRiskAssessment.workContent || '');
            setContractor(existingRiskAssessment.contractor || '');
            setSubcontractor(existingRiskAssessment.subcontractor || '');

            if (existingRiskAssessment.competentPerson) {
                setCompetentPerson({
                    name: existingRiskAssessment.competentPerson.name || '',
                    certificateNumber: existingRiskAssessment.competentPerson.certificateNumber || '',
                    expiryDate: existingRiskAssessment.competentPerson.expiryDate ?
                        new Date(existingRiskAssessment.competentPerson.expiryDate).toLocaleDateString('en-CA') : '',
                });
            }

            if (existingRiskAssessment.riskAssessmentItems && Array.isArray(existingRiskAssessment.riskAssessmentItems)) {
                const validatedItems = existingRiskAssessment.riskAssessmentItems.map((item: any) => ({
                    ...item,
                    assessmentResult: {
                        yes: item.assessmentResult?.yes || false,
                        no: item.assessmentResult?.no || false,
                        reason: item.assessmentResult?.reason || ''
                    },
                    defaultSafetyPrecautions: Array.isArray(item.defaultSafetyPrecautions)
                        ? item.defaultSafetyPrecautions.map((precaution: any) => ({
                            item: precaution.item || '',
                            content: precaution.content || '',
                            isChecked: precaution.isChecked || false
                        }))
                        : [],
                    extraSafetyPrecautions: Array.isArray(item.extraSafetyPrecautions)
                        ? item.extraSafetyPrecautions.map((precaution: any) => ({
                            item: precaution.item || '',
                            content: precaution.content || ''
                        }))
                        : [],
                    severity: item.severity || 0,
                    likelihood: item.likelihood || 0,
                    riskRating: item.riskRating || 0,
                    stayHour: Number(item.stayHour) || 0
                }));
                setRiskAssessmentItems(validatedItems);
            }

            if (existingRiskAssessment.safetyPrecautionsChecklist) {
                setSafetyPrecautionsChecklist(existingRiskAssessment.safetyPrecautionsChecklist);
            }

            if (existingRiskAssessment.assessmentSignature) {
                setAssessmentSignature({
                    name: existingRiskAssessment.assessmentSignature.name || '',
                    dateTime: existingRiskAssessment.assessmentSignature.dateTime ?
                        new Date(existingRiskAssessment.assessmentSignature.dateTime).toISOString().slice(0, 16) : '',
                    sign: existingRiskAssessment.assessmentSignature.sign || '',
                    signUrl: existingRiskAssessment.assessmentSignature.signUrl || ''
                });
            }

            if (existingRiskAssessment.reportReceiptSignature) {
                setReportReceiptSignature({
                    name: existingRiskAssessment.reportReceiptSignature.name || '',
                    position: existingRiskAssessment.reportReceiptSignature.position || '',
                    dateTime: existingRiskAssessment.reportReceiptSignature.dateTime ?
                        new Date(existingRiskAssessment.reportReceiptSignature.dateTime).toISOString().slice(0, 16) : '',
                    sign: existingRiskAssessment.reportReceiptSignature.sign || '',
                    signUrl: existingRiskAssessment.reportReceiptSignature.signUrl || ''
                });
            }
        }
    }, [currentMode, existingRiskAssessment, isLoading]);

    // 下載風險評估報告
    const handleDownloadReport = async () => {
        if (!scheduleID) {
            alert('無法下載：缺少工作排程 ID');
            return;
        }

        try {
            const blob = await getRiskAssessmentReport(scheduleID).unwrap();

            // 創建下載連結
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // 設置檔案名稱
            const fileName = `RiskAssessment_${scheduleID}_${dayjs().format('YYYY-MM-DD')}.pdf`;
            link.download = fileName;

            // 觸發下載
            document.body.appendChild(link);
            link.click();

            // 清理
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('下載風險評估報告失敗:', error);
            alert('下載失敗，請稍後再試');
        }
    };

    return {
        // 狀態
        site,
        setSite,
        workContent,
        setWorkContent,
        contractor,
        setContractor,
        subcontractor,
        setSubcontractor,
        competentPerson,
        setCompetentPerson,
        assessmentSignature,
        setAssessmentSignature,
        reportReceiptSignature,
        setReportReceiptSignature,
        riskAssessmentItems,
        setRiskAssessmentItems,
        safetyPrecautionsChecklist,
        setSafetyPrecautionsChecklist,

        // 模式相關
        currentMode,
        isViewMode,
        scheduleID,

        // API 相關
        isLoading,
        error,

        // 下載功能
        handleDownloadReport
    };
};